import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Portal } from './Portal';
import { useT } from '../i18n';

type ExclusionReason =
  | 'OPTED_OUT' | 'SUPPRESSED' | 'INVALID_NUMBER' | 'TEMP_FAILURE'
  | 'NO_CONSENT' | 'COOLDOWN' | 'FREQUENCY_CAP';

type Engagement = 'ENGAGED' | 'ACTIVE' | 'INACTIVE' | 'NEW';

interface Preflight {
  total: number;
  eligible: number;
  validWhatsApp: number;
  unknownWhatsApp: number;
  excluded: Record<ExclusionReason, number>;
  engagement: Record<Engagement, number>;
  rules: { cooldownHours: number; frequencyCapCount: number; frequencyCapDays: number; requireConsent: boolean };
  at: string;
}

interface ExcludedContact {
  id: string;
  contactName: string;
  contactPhone: string;
  skipReason: ExclusionReason | null;
}

const REASON_ORDER: ExclusionReason[] = [
  'OPTED_OUT', 'SUPPRESSED', 'INVALID_NUMBER', 'TEMP_FAILURE', 'NO_CONSENT', 'COOLDOWN', 'FREQUENCY_CAP',
];

// O tom diz quanto é definitivo: vermelho não volta sozinho, âmbar volta com o tempo
const REASON_TONE: Record<ExclusionReason, string> = {
  OPTED_OUT: 'bg-red-50 text-red-800 border-red-200',
  SUPPRESSED: 'bg-red-50 text-red-800 border-red-200',
  INVALID_NUMBER: 'bg-orange-50 text-orange-800 border-orange-200',
  TEMP_FAILURE: 'bg-amber-50 text-amber-800 border-amber-200',
  NO_CONSENT: 'bg-slate-50 text-slate-700 border-slate-200',
  COOLDOWN: 'bg-sky-50 text-sky-800 border-sky-200',
  FREQUENCY_CAP: 'bg-indigo-50 text-indigo-800 border-indigo-200',
};

const ENGAGEMENT_ORDER: Engagement[] = ['ENGAGED', 'ACTIVE', 'INACTIVE', 'NEW'];

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const formatNumber = (value: number) => value.toLocaleString('en-US');

export function CampaignAudienceModal({ campaignId, campaignName, onClose }: {
  campaignId: string;
  campaignName?: string;
  onClose: () => void;
}) {
  const t = useT();
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const [loading, setLoading] = useState(true);
  const [openReason, setOpenReason] = useState<ExclusionReason | null>(null);
  const [contacts, setContacts] = useState<ExcludedContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/preflight${refresh ? '?refresh=1' : ''}`, { headers: authHeaders() });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      setPreflight(data.preflight);
    } catch {
      toast.error(t('aud.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [campaignId, t]);

  useEffect(() => { load(); }, [load]);

  const openList = async (reason: ExclusionReason) => {
    if (openReason === reason) {
      setOpenReason(null);
      return;
    }
    setOpenReason(reason);
    setContactsLoading(true);
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/excluded?reason=${reason}&pageSize=50`, { headers: authHeaders() });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      setContacts(data.items || []);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const excludedTotal = preflight
    ? REASON_ORDER.reduce((total, reason) => total + (preflight.excluded[reason] || 0), 0)
    : 0;

  return (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate" dir="auto">
                {t('aud.title')}{campaignName ? ` · ${campaignName}` : ''}
              </h3>
              <p className="text-xs text-gray-500 mt-1">{t('aud.subtitle')}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => load(true)} disabled={loading} className="text-sm px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                ↻ {t('aud.refresh')}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2" title={t('conn.close')}>✕</button>
            </div>
          </div>

          {loading && !preflight && <p className="p-12 text-center text-gray-400">{t('common.loading')}</p>}

          {preflight && (
            <div className={`p-6 space-y-5 ${loading ? 'opacity-60' : ''}`}>
              {/* O número que importa, e de onde ele saiu */}
              <div className="rounded-xl border-2 border-emerald-500 bg-emerald-50 p-5 text-center">
                <p className="text-sm text-emerald-800">{t('aud.eligible')}</p>
                <p className="text-4xl font-bold text-emerald-700 mt-1">{formatNumber(preflight.eligible)}</p>
                <p className="text-xs text-emerald-800 mt-1">
                  {t('aud.total')}: {formatNumber(preflight.total)} · {t('aud.validWhatsApp')}: {formatNumber(preflight.validWhatsApp)}
                  {preflight.unknownWhatsApp > 0 && ` · ${t('aud.unknownWhatsApp')}: ${formatNumber(preflight.unknownWhatsApp)}`}
                </p>
              </div>

              {/* Excluídos, um motivo por linha, cada um abrindo a lista */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">
                  {t('aud.excludedTitle')} <span className="text-gray-400 font-normal">({formatNumber(excludedTotal)})</span>
                </h4>

                {excludedTotal === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center rounded-lg border border-dashed border-gray-200">
                    {t('aud.noExclusions')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {REASON_ORDER.filter(reason => (preflight.excluded[reason] || 0) > 0).map(reason => (
                      <div key={reason}>
                        <button
                          onClick={() => openList(reason)}
                          className={`w-full flex items-center justify-between gap-3 border rounded-lg px-4 py-2.5 text-start hover:brightness-95 ${REASON_TONE[reason]}`}
                        >
                          <span className="text-sm font-medium">{t(`ex.${reason}`)}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            <span className="font-bold">{formatNumber(preflight.excluded[reason])}</span>
                            <span className="text-xs opacity-70">
                              {openReason === reason ? t('aud.hideList') : t('aud.showList')}
                            </span>
                          </span>
                        </button>

                        {openReason === reason && (
                          <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden">
                            {contactsLoading ? (
                              <p className="p-4 text-center text-sm text-gray-400">{t('common.loading')}</p>
                            ) : contacts.length === 0 ? (
                              <p className="p-4 text-center text-sm text-gray-400">{t('aud.emptyList')}</p>
                            ) : (
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500">
                                  <tr>
                                    <th className="text-start px-4 py-2">{t('aud.colContact')}</th>
                                    <th className="text-start px-4 py-2">{t('aud.colReason')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contacts.map(contact => (
                                    <tr key={contact.id} className="border-t">
                                      <td className="px-4 py-2">
                                        <span className="block text-gray-900" dir="auto">{contact.contactName}</span>
                                        <span className="block text-xs text-gray-500" dir="ltr">{contact.contactPhone}</span>
                                      </td>
                                      <td className="px-4 py-2 text-gray-600">
                                        {contact.skipReason ? t(`ex.${contact.skipReason}`) : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('aud.engagementTitle')}</h4>
                  <div className="space-y-1.5">
                    {ENGAGEMENT_ORDER.map(level => (
                      <div key={level} className="flex justify-between text-sm">
                        <span className="text-gray-600">{t(`eng.${level}`)}</span>
                        <span className="font-semibold text-gray-900">{formatNumber(preflight.engagement[level] || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('aud.rulesTitle')}</h4>
                  <ul className="space-y-1.5 text-sm text-gray-600">
                    <li>• {t('aud.ruleCooldown', { hours: preflight.rules.cooldownHours })}</li>
                    <li>• {t('aud.ruleFrequency', { count: preflight.rules.frequencyCapCount, days: preflight.rules.frequencyCapDays })}</li>
                    {preflight.rules.requireConsent && <li>• {t('aud.ruleConsent')}</li>}
                  </ul>
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center">
                {t('aud.at', { time: new Date(preflight.at).toLocaleString() })}
              </p>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
